// ★★ AUTO-RECOVERY FROM A STALE VITE CHUNK (2026-09-24).
//
// Every deploy publishes new content-hashed chunk filenames and deletes the old ones. A tab that
// was already open (or one restored from bfcache / the back-forward cache) still holds the entry
// code from BEFORE the deploy, so the moment it runs a lazy `import()` — opening a modal, a tab, a
// picker, mounting a Vue app — it asks for a hash the server no longer has, gets a 404, and the
// import rejects:
//
//   TypeError: Failed to fetch dynamically imported module: .../vite/assets/choices_....js
//   Error: Unable to preload CSS for /vite/assets/InventoryGallery.7244ca99.css
//
// PostHog `$exception` measured this at 12 of 23 client-side exceptions in 36 hours, clustered
// exactly at the deploy times (02:53 and 03:28 on consecutive days), on /deals,
// /inventory/:slug/inventory_costs and /inventory/:slug/photos. The dealer clicks and nothing
// happens — the same family as the 2026-09-10 bug where a failed controller chunk stayed dead for
// the life of the page ([[rageclick-fixes-2026-09-10]]).
//
// Retrying a URL that a deploy deleted can never succeed. The only cure is to reload and pick up
// the new manifest, so that is what this does — ONCE.
//
// ★★ THE DANGER IS THE LOOP, NOT THE BUG. A bad deploy plus an unconditional reload is an infinite
// refresh on every dealer's browser, which is far worse than a dead modal. Hence: a timestamp in
// sessionStorage so at most one reload happens per RELOAD_WINDOW_MS, a per-page-load flag so
// concurrent rejections can't stack reloads, and — importantly — NO automatic reload at all when
// we cannot read/write that guard (private mode, blocked site data). No guard, no reload; the
// dealer gets the "a new version was released" bar and reloads by hand instead.
//
// ★ FOOTGUN (why the listeners are registered at module top level): `DOMContentLoaded` may already
// have fired by the time an ESM module runs, so a listener registered inside it never fires. See
// [[key-learnings-2026-06]] / deal_pricing_adjustment_fix.
//
// ★ FOOTGUN (Vite version): `vite:preloadError` only exists from Vite 4.1; this app is pinned at
// 4.0.3, where the preload helper just rejects (grep `Unable to preload CSS` in
// node_modules/vite/dist/node/chunks — it constructs that Error and nothing dispatches an event).
// So `unhandledrejection` is the load-bearing path today; the `vite:preloadError` listener below
// is wired anyway so a future Vite bump is covered for free without anyone remembering this.

const STALE_CHUNK_RELOAD_GUARD_KEY = 'ml:stale-chunk-reload-at'
const STALE_CHUNK_RELOAD_COUNT_KEY = 'ml:stale-chunk-reload-count'
const STALE_CHUNK_RELOAD_WINDOW_MS = 10000
// ★ The time window alone is not airtight: if a page takes LONGER than the window to get as far as
// the failing import (slow connection, big page), every reload would clear the window and we would
// have a slow but real refresh loop. So there is a hard ceiling per tab session as well. Three is
// enough for a dealer who leaves a tab open across several deploys in a day; past that they get the
// "a new version was released" bar and reload by hand, which is where we were before this file.
const STALE_CHUNK_RELOAD_MAX_PER_SESSION = 3

// Vite, Chrome, Safari and Firefox all word this differently; match the family, not one sentence.
const STALE_CHUNK_MESSAGE_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i

// Per page load: once we have asked for a reload, every later rejection is noise.
let staleChunkReloadStarted = false
let staleAssetNoticeShown = false

function staleChunkErrorMessage(error) {
  if (!error) return ''
  if (typeof error === 'string') return error
  try {
    return String(error.message || error)
  } catch (e) {
    return ''
  }
}

function looksLikeStaleChunk(error) {
  return STALE_CHUNK_MESSAGE_RE.test(staleChunkErrorMessage(error))
}

// ★ Returns null — NOT 0 — when sessionStorage is unusable. `window.sessionStorage` THROWS (it does
// not return null) when site data is blocked, so "no guard available" has to be distinguishable
// from "guard says we have never reloaded", or private-mode users get the infinite loop.
function readStaleChunkGuard() {
  try {
    const at = parseInt(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_GUARD_KEY), 10)
    const count = parseInt(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_COUNT_KEY), 10)
    return { at: isNaN(at) ? 0 : at, count: isNaN(count) ? 0 : count }
  } catch (e) {
    return null
  }
}

function recordStaleChunkReload(at, count) {
  try {
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_GUARD_KEY, String(at))
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_COUNT_KEY, String(count))
    return true
  } catch (e) {
    return false
  }
}

// Reloading out from under someone who is typing throws their work away. Only TEXT entry counts:
// a focused <select> is the normal state when the chunk that was meant to upgrade it (choices) is
// the thing that failed, and refusing to heal that case would defeat the fix.
function userIsTyping() {
  try {
    const element = document.activeElement
    if (!element) return false
    if (element.isContentEditable) return true

    const tag = (element.tagName || '').toLowerCase()
    if (tag === 'textarea') return true
    if (tag !== 'input') return false

    const type = (element.getAttribute && (element.getAttribute('type') || '') || '').toLowerCase()
    return !['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'range', 'hidden'].includes(type)
  } catch (e) {
    return false
  }
}

// The decision is split out from the doing so it can be tested without a browser, and so the
// reason is reportable. Every branch that is NOT 'reload' leaves the page alone.
function staleChunkReloadDecision(error) {
  if (!looksLikeStaleChunk(error)) return 'not-stale'
  if (staleChunkReloadStarted) return 'already-reloading'

  // Offline is not a stale deploy: reloading would replace a working page with the browser's
  // "no internet" error. Wait for the chunk to be fetchable again instead.
  try {
    if (window.navigator && window.navigator.onLine === false) return 'offline'
  } catch (e) { /* no navigator — assume online */ }

  try {
    if (document.visibilityState && document.visibilityState !== 'visible') return 'not-visible'
  } catch (e) { /* no visibilityState — assume visible */ }

  if (userIsTyping()) return 'user-editing'

  const guard = readStaleChunkGuard()
  if (guard === null) return 'no-storage'

  const now = Date.now()
  // `now < guard.at` = a clock that moved backwards (or a stored value from the future). Treat it
  // as "we just reloaded" — suppressing a reload is always the safe side of this comparison.
  if (guard.at && (now - guard.at < STALE_CHUNK_RELOAD_WINDOW_MS || now < guard.at)) return 'reloaded-recently'
  if (guard.count >= STALE_CHUNK_RELOAD_MAX_PER_SESSION) return 'reload-budget-spent'

  if (!recordStaleChunkReload(now, guard.count + 1)) return 'no-storage'

  return 'reload'
}

function reportStaleChunk(decision, error, source) {
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('stale_chunk_recovery', {
        decision: decision,
        source: source || 'unknown',
        message: staleChunkErrorMessage(error).slice(0, 300)
      })
    }
  } catch (e) { /* telemetry must never be the thing that breaks the page */ }
  try { console.warn('[stale-chunk]', decision, source || '', staleChunkErrorMessage(error)) } catch (e) {}
}

// Shown ONCE, and only when we did not (or must not) reload automatically. A dealer whose page has
// quietly lost a feature cannot diagnose that, and no retry of a deleted URL will ever work.
function showStaleAssetNotice() {
  if (staleAssetNoticeShown) return
  staleAssetNoticeShown = true
  try {
    if (document.getElementById('ml-stale-assets-notice')) return
    const bar = document.createElement('div')
    bar.id = 'ml-stale-assets-notice'
    bar.setAttribute('role', 'status')
    bar.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483000;' +
      'background:#0b5175;color:#fff;padding:10px 14px;font:14px/1.4 system-ui,sans-serif;' +
      'display:flex;gap:12px;align-items:center;justify-content:center;box-shadow:0 -2px 8px rgba(0,0,0,.2)'
    const text = document.createElement('span')
    text.textContent = 'A new version of MotorLot was released. Reload the page so everything on it works.'
    const reload = document.createElement('button')
    reload.type = 'button'
    reload.textContent = 'Reload'
    reload.style.cssText = 'background:#fff;color:#0b5175;border:0;border-radius:6px;' +
      'padding:6px 12px;font-weight:600;cursor:pointer'
    reload.addEventListener('click', function () { window.location.reload() })
    bar.appendChild(text)
    bar.appendChild(reload)
    ;(document.body || document.documentElement).appendChild(bar)
  } catch (e) { /* a notice that throws would be worse than no notice */ }
}

// The one entry point. Returns the decision string so callers (and the spec) can see what happened.
// `notice: false` suppresses the fallback bar — the Stimulus registry uses that to keep its
// "don't blame a release until the second failure" rule.
function handleStaleChunkError(error, options) {
  const opts = options || {}
  const decision = staleChunkReloadDecision(error)

  if (decision === 'not-stale') return decision

  reportStaleChunk(decision, error, opts.source)

  if (decision === 'reload') {
    staleChunkReloadStarted = true
    try {
      window.location.reload()
    } catch (e) {
      // Could not even reload — fall back to telling the dealer.
      showStaleAssetNotice()
    }
    return decision
  }

  if (decision !== 'already-reloading' && opts.notice !== false) showStaleAssetNotice()

  return decision
}

// ---- listeners (top level on purpose; see the DOMContentLoaded footgun above) ----------------

function staleChunkRejectionListener(event) {
  const reason = event && ('reason' in event ? event.reason : event.detail && event.detail.reason)
  handleStaleChunkError(reason, { source: 'unhandledrejection' })
}

function vitePreloadErrorListener(event) {
  handleStaleChunkError(event && (event.payload || event.detail), { source: 'vite:preloadError' })
}

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('unhandledrejection', staleChunkRejectionListener)
  window.addEventListener('vite:preloadError', vitePreloadErrorListener)
}

export { handleStaleChunkError, looksLikeStaleChunk, showStaleAssetNotice }
